import {computed, nextTick, onMounted, watch} from "vue";
import {useEventListener, useResizeObserver, useWindowSize} from "@vueuse/core";

const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line

// Helper: Calculate the parent width
function getParentWidth(element) {
  const parent = element.parentElement;
  if (!parent) {
    log("useMaxFontSize: No parent element found.");
    return 0;
  }
  return parent.offsetWidth;
}

// Helper: Dynamically calculate the maximum font size
function getMaxFontSize(parentWidth, viewportWidth, maxFontSize = 100) {
  return Math.min(maxFontSize, parentWidth * 0.1, viewportWidth * 0.05); // narrow screens smaller
}

// Helper: Incrementally adjust to the largest possible font size with fine precision
function adjustFontSizeToFit(element, parentWidth, minFontSize, maxFontSize, hasBuffer = true) {
  let low = minFontSize;
  let high = maxFontSize;
  let bestFitFontSize = minFontSize;

  // Binary search to find the largest whole-pixel font size that fits
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    element.style.fontSize = `${mid}px`;

    if (element.scrollWidth <= parentWidth) {
      bestFitFontSize = mid; // Update best fit size
      low = mid + 1; // Try larger sizes
    } else {
      high = mid - 1; // Reduce size
    }
  }

  // Apply best fit size initially
  element.style.fontSize = `${bestFitFontSize}px`;

  // Check for slight overflows with a buffer (final safeguard)
  // (not with fillsWidth: an element as wide as its parent always "overflowed" by it, 1px less)
  const buffer = 1; // Allow for a 1px buffer
  if (hasBuffer && element.scrollWidth > parentWidth - buffer) {
    bestFitFontSize--; // Revert to a slightly smaller size
    element.style.fontSize = `${bestFitFontSize}px`;
  }

  return bestFitFontSize;
}

function adjustFontSize(element, options = {}) {
  if (!element) return log("useMaxFontSize: No element provided.");
  log(`useMaxFontSize: Adjusting element: ${element.tagName}`);

  // Ensure text does not wrap
  element.style.whiteSpace = "nowrap";
  element.style.overflow = "hidden";

  const parentWidth = getParentWidth(element);
  if (parentWidth <= 0) return log("useMaxFontSize: Parent width invalid.");

  const viewportWidth = window.innerWidth;
  // fillsWidth: the largest size that fits, up to maxFontSize (no proportional caps)
  const maxFontSize = options.fillsWidth ? options.maxFontSize || 100
    : getMaxFontSize(parentWidth, viewportWidth, options.maxFontSize);
  const minFontSize = 8; // px

  const finalFontSize = adjustFontSizeToFit(element, parentWidth, minFontSize, maxFontSize,
    !options.fillsWidth);
  log(`useMaxFontSize: Final font size for element: ${finalFontSize}px`);
}

// options.maxFontSize: a cap in px (default 100); options.fillsWidth: as large as fits, without
// the default caps (10% of the parent's width, 5% of the window's), refit whenever the parent's
// width changes; options.isActive: a getter, for an element in a dialog that's always in the
// page (closed, it has no width): each time it turns true (the dialog opening), fit again
// (2026-10-14, as useAutoFocus's). Fit again too once the web fonts load: measured in a fallback
// font, the size was off (2026-10-22, Firefox)
export function useMaxFontSize(element, options = {}) {
  const adjust = () => {
    if (element.value) {
      adjustFontSize(element.value, options);
    } else {
      log("useMaxFontSize: Element reference is not available.");
    }
  };
  onMounted(() => {
    const {width} = useWindowSize(); // Reactive window size
    adjust(); // Initial adjustment
    watch(width, adjust); // React to viewport changes
    document.fonts?.ready.then(adjust);
  });
  if (document.fonts) useEventListener(document.fonts, "loadingdone", adjust);
  // the parent's width, not the element's: a fitted element has no width of its own
  let parentWidth = 0;
  const parent = computed(() => (options.fillsWidth ? element.value?.parentElement : null));
  useResizeObserver(parent, ([entry]) => {
    if (Math.round(entry.contentRect.width) === parentWidth) return;
    parentWidth = Math.round(entry.contentRect.width);
    adjust();
  });
  if (options.isActive) {
    watch(options.isActive, async (isNowActive) => {
      if (!isNowActive) return;
      await nextTick();
      requestAnimationFrame(adjust); // once the dialog is open and laid out
    }, {flush: "post"});
  }
}
